import type { CatalogCategory, CatalogProduct } from "../comparison-catalog";
import { canonicalJson } from "./canonical-json";

export type CatalogSnapshot = {
  schemaVersion: 1;
  category: CatalogCategory;
  products: CatalogProduct[];
  reviewedAt: string;
};
export type CatalogEdition = CatalogSnapshot & {
  origin: "editorial" | "atlas";
  version: string;
  contentHash: string | null;
  stale?: { cachedAt: string; ageSeconds: number };
};

const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const text = (v: unknown, max = 4000): v is string => typeof v === "string" && v.length > 0 && v.length <= max;
const slug = (v: unknown): v is string => text(v, 120) && /^[a-z0-9][a-z0-9._-]*$/.test(v);
const https = (v: unknown) => { try { const u = new URL(String(v)); return u.protocol === "https:" && !u.username && !u.password; } catch { return false; } };

/** Reject the entire edition, never merge a partial snapshot with seed values. */
export function parseCatalogSnapshot(value: unknown, expected: CatalogCategory): CatalogSnapshot {
  const fail = (): never => { throw new Error("CUSTOMY_CATALOG_INVALID"); };
  if (!record(value) || value.schemaVersion !== 1 || !record(value.category)) return fail();
  // Metric/schema changes require a coordinated application release and review.
  try { if (canonicalJson(value.category) !== canonicalJson(expected)) return fail(); }
  catch { return fail(); }
  if (!text(value.reviewedAt, 10) || !/^\d{4}-\d{2}-\d{2}$/.test(value.reviewedAt) || !Number.isFinite(Date.parse(value.reviewedAt))) return fail();
  if (!Array.isArray(value.products) || value.products.length > 2000) return fail();
  const ids = new Set<string>();
  for (const p of value.products) {
    if (!record(p) || !slug(p.id) || ids.has(p.id) || p.category !== expected.id) return fail();
    ids.add(p.id);
    if (p.reviewedAt !== undefined && (!text(p.reviewedAt, 10) || !/^\d{4}-\d{2}-\d{2}$/.test(p.reviewedAt)
      || !Number.isFinite(Date.parse(p.reviewedAt)) || new Date(p.reviewedAt).toISOString().slice(0, 10) !== p.reviewedAt)) return fail();
    if (![p.name, p.provider, p.kind, p.summary, p.caveat].every(v => text(v)) || !text(p.color, 7) || !/^#[0-9a-fA-F]{6}$/.test(p.color)) return fail();
    if (!record(p.values) || !record(p.specs) || !Object.entries(p.specs).every(([k, v]) => text(k, 160) && text(v))) return fail();
    if (!Array.isArray(p.sources) || !p.sources.length || p.sources.length > 100) return fail();
    for (const s of p.sources) {
      if (!record(s) || !text(s.label, 500) || !https(s.url) || !Array.isArray(s.fields) || !s.fields.every(v => text(v, 120))) return fail();
    }
    for (const metric of expected.metrics) {
      const v = p.values[metric.key];
      if (v !== null && (typeof v !== "number" || !Number.isFinite(v) || v < 0)) return fail();
      if (v !== null && !p.sources.some(s => (s as { fields: string[] }).fields.includes(metric.key))) return fail();
    }
  }
  return value as CatalogSnapshot;
}

export function selectEditionProducts(edition: CatalogSnapshot, raw?: string | null): CatalogProduct[] {
  const ids = raw == null ? edition.category.defaults : raw.split(",");
  return [...new Set(ids)].map(id => edition.products.find(p => p.id === id)).filter((p): p is CatalogProduct => !!p).slice(0, 4);
}
